/**
 * Tree shapes both sides need (see Plant.tree): kept apart from the rest of
 * the shared code so the client's tree worker (treeWorker) can load just this,
 * and not everything (the physics, its WebAssembly) the package index brings.
 */
import { v2, type Vec2 } from "../math";
import { Rng } from "./rng";

/** How a tree looks (the client draws it as pixel art from this, see treeArt.ts): its species. */
export type TreeStyle =
  | "oak" | "pine" | "birch" | "willow" | "lantern" | "frost" | "dusk"
  | "candelabra" | "pagoda" | "stilt" | "braid" | "bell" | "thorn" | "halo" | "kelp" | "cloud"
  | "starbark" | "palm" | "acacia" | "cypress" | "capTree" | "ember" | "antler" | "baobab";

/**
 * Each species: whose trunk it grows (treeTrunk: a pine's runs nearly to its
 * tip, a birch's is slender, the rest an oak's), and where its crown is, as
 * shares of its height: how far up its middle sits and how big it is round
 * (where leaves shake loose from, and gas bags graze). A bare one has no
 * leaves to drop.
 */
const SPECIES: Record<TreeStyle, { trunk: "oak" | "pine" | "birch"; up: number; size: number; bare?: true }> = {
  oak: { trunk: "oak", up: 0.7, size: 0.28 },
  pine: { trunk: "pine", up: 0.7, size: 0.28 },
  birch: { trunk: "birch", up: 0.7, size: 0.28 },
  willow: { trunk: "oak", up: 0.7, size: 0.28 },
  lantern: { trunk: "oak", up: 0.7, size: 0.28 },
  frost: { trunk: "oak", up: 0.7, size: 0.28 },
  dusk: { trunk: "oak", up: 0.7, size: 0.28 },
  candelabra: { trunk: "oak", up: 0.8, size: 0.25 },
  pagoda: { trunk: "pine", up: 0.6, size: 0.3 },
  stilt: { trunk: "birch", up: 0.65, size: 0.3 },
  braid: { trunk: "oak", up: 0.68, size: 0.25 },
  bell: { trunk: "oak", up: 0.5, size: 0.3 },
  thorn: { trunk: "birch", up: 0.65, size: 0.25 },
  halo: { trunk: "oak", up: 0.72, size: 0.23 },
  kelp: { trunk: "pine", up: 0.55, size: 0.35 },
  cloud: { trunk: "oak", up: 0.6, size: 0.3 },
  starbark: { trunk: "oak", up: 0.7, size: 0.28 },
  palm: { trunk: "birch", up: 0.55, size: 0.3 },
  acacia: { trunk: "oak", up: 0.75, size: 0.35 },
  cypress: { trunk: "pine", up: 0.45, size: 0.3 },
  capTree: { trunk: "oak", up: 0.6, size: 0.4 },
  ember: { trunk: "oak", up: 0.7, size: 0.28, bare: true },
  antler: { trunk: "birch", up: 0.7, size: 0.28, bare: true },
  baobab: { trunk: "oak", up: 0.65, size: 0.22 },
};
export const TREE_STYLES: readonly string[] = Object.keys(SPECIES);

/** Where a tree's crown is (see SPECIES): its middle `up` blocks up from the root, `size` blocks round; bare if it has no leaves. */
export function treeCrown(style: TreeStyle, blocks: number): { up: number; size: number; bare: boolean } {
  const s = SPECIES[style];
  return { up: blocks * s.up, size: blocks * s.size, bare: !!s.bare };
}

/** A tree's trunk: a curve in the tree's own frame (blocks; x across, y up from its root), and its half-width at the root. */
export interface TreeTrunk {
  /** A cubic: from the root, pulled toward the middle two, to the top. */
  pts: [Vec2, Vec2, Vec2, Vec2];
  half: number;
}

/**
 * How a tree's trunk runs (see Plant.tree), from its style, seed and size:
 * curving one way then back the other as it rises, a pine's nearly to its
 * tip, the rest about half way, where their branches spread (each species
 * grows one of three trunks, see SPECIES). The client draws it (treeArt) and the trunk tiles you swing at follow it, so both
 * agree.
 */
export function treeTrunk(style: TreeStyle, seed: number, blocks: number): TreeTrunk {
  const rng = new Rng(seed ^ 0x7e3c);
  const kind = SPECIES[style].trunk;
  const pine = kind === "pine";
  const h = blocks * (pine ? 0.92 : rng.range(0.5, 0.6));
  const sway = blocks * (pine ? rng.range(0.05, 0.1) : rng.range(0.12, 0.22)) * (rng.next() < 0.5 ? -1 : 1);
  const topX = blocks * rng.range(-0.12, 0.12);
  const half = Math.max(1.1, Math.sqrt(blocks / 12) * (kind === "birch" ? 0.68 : pine ? 0.8 : 0.92));
  return { pts: [v2(0, 0), v2(sway, h * 0.33), v2(topX - sway * rng.range(0.6, 1.1), h * 0.68), v2(topX, h)], half };
}

/** The point at `t` (0 at the root, 1 at the top) along a trunk. */
export function trunkPoint(trunk: TreeTrunk, t: number): Vec2 {
  const [a, b, c, d] = trunk.pts;
  const u = 1 - t;
  return v2(u * u * u * a.x + 3 * u * u * t * b.x + 3 * u * t * t * c.x + t * t * t * d.x, u * u * u * a.y + 3 * u * u * t * b.y + 3 * u * t * t * c.y + t * t * t * d.y);
}
